/**
 * One region per place, however it was typed.
 *
 * A member's location is whatever they wrote on the registration form, and the
 * directory's region filter listed each spelling as its own region: Brooklyn
 * ten ways, New York twenty-five, 218 strings for about 130 places. Tracy
 * Bartley, 11 September: "I only show up in the specific 'region' I signed up
 * for - and not for the other incantations of Los Angeles."
 *
 * So the filter groups by place, not by string. What the member wrote is left
 * alone on their card: this decides which region an entry belongs to, never
 * what it says. A US place is "City, ST", anywhere else "City, Country", and
 * somebody who lists two places ("New York / Vienna") is in both.
 */

const US_STATES: Record<string, string> = {
  alabama: "AL", alaska: "AK", arizona: "AZ", arkansas: "AR", california: "CA",
  colorado: "CO", connecticut: "CT", delaware: "DE", "district of columbia": "DC",
  florida: "FL", georgia: "GA", hawaii: "HI", idaho: "ID", illinois: "IL",
  indiana: "IN", iowa: "IA", kansas: "KS", kentucky: "KY", louisiana: "LA",
  maine: "ME", maryland: "MD", massachusetts: "MA", michigan: "MI",
  minnesota: "MN", mississippi: "MS", missouri: "MO", montana: "MT",
  nebraska: "NE", nevada: "NV", "new hampshire": "NH", "new jersey": "NJ",
  "new mexico": "NM", "new york": "NY", "north carolina": "NC",
  "north dakota": "ND", ohio: "OH", oklahoma: "OK", oregon: "OR",
  pennsylvania: "PA", "rhode island": "RI", "south carolina": "SC",
  "south dakota": "SD", tennessee: "TN", texas: "TX", utah: "UT", vermont: "VT",
  virginia: "VA", washington: "WA", "west virginia": "WV", wisconsin: "WI",
  wyoming: "WY", "puerto rico": "PR",
};
const STATE_CODES = new Set(Object.values(US_STATES));

// Spellings of a country, lower-cased with dots removed, and the one name the
// region uses. "US" is recognised so it can be dropped: a US place is named by
// its state.
const COUNTRY_ALIASES: Record<string, string> = {
  us: "US", usa: "US", "united states": "US", "united states of america": "US",
  america: "US",
  uk: "UK", "united kingdom": "UK", england: "UK", scotland: "UK", wales: "UK",
  "great britain": "UK", britain: "UK", gb: "UK",
  holland: "Netherlands", "the netherlands": "Netherlands",
  deutschland: "Germany",
};
const COUNTRIES = new Set(
  [
    "Argentina", "Australia", "Austria", "Belgium", "Brazil", "Canada", "Chile",
    "China", "Colombia", "Croatia", "Cuba", "Czech Republic", "Denmark", "Egypt",
    "Finland", "France", "Germany", "Greece", "Hong Kong", "Hungary", "Iceland",
    "India", "Indonesia", "Ireland", "Israel", "Italy", "Jamaica", "Japan",
    "Kenya", "Korea", "South Korea", "Lebanon", "Luxembourg", "Mexico", "Morocco",
    "Netherlands", "New Zealand", "Nigeria", "Norway", "Peru", "Philippines",
    "Poland", "Portugal", "Puerto Rico", "Romania", "Russia", "Senegal",
    "Singapore", "South Africa", "Spain", "Sweden", "Switzerland", "Taiwan",
    "Thailand", "Trinidad and Tobago", "Turkey", "Ukraine",
    "United Arab Emirates", "Uruguay", "Venezuela", "Vietnam",
  ].map((c) => c.toLowerCase())
);

// Canada's provinces, which a member writes where an American writes a state.
// None of the codes is also a US state's.
const CANADIAN_PROVINCES = new Set([
  "alberta", "british columbia", "manitoba", "new brunswick",
  "newfoundland and labrador", "nova scotia", "ontario", "prince edward island",
  "quebec", "québec", "saskatchewan", "yukon", "northwest territories", "nunavut",
  "ab", "bc", "mb", "nb", "nl", "ns", "on", "pe", "qc", "sk", "yt", "nt", "nu",
]);

// The same city by another name, with the state it is in. Kept to the ones
// members actually write. Brooklyn and Manhattan stay themselves: somebody who
// wrote Brooklyn meant it.
const CITY_ALIASES: Record<string, [string, string]> = {
  nyc: ["New York", "NY"],
  "new york city": ["New York", "NY"],
  ny: ["New York", "NY"],
  la: ["Los Angeles", "CA"],
  sf: ["San Francisco", "CA"],
  dc: ["Washington", "DC"],
};

type Place = { city: string; qualifier: string };

const squash = (s: string) => s.replace(/\s+/g, " ").trim();
const bare = (s: string) => squash(s).toLowerCase().replace(/\./g, "");

function countryOf(token: string): string | undefined {
  const key = bare(token);
  if (COUNTRY_ALIASES[key]) return COUNTRY_ALIASES[key];
  if (COUNTRIES.has(key)) return titleCase(key);
  if (CANADIAN_PROVINCES.has(key)) return "Canada";
  return undefined;
}

function stateOf(token: string): string | undefined {
  const key = bare(token);
  if (US_STATES[key]) return US_STATES[key];
  const code = key.toUpperCase();
  return key.length === 2 && STATE_CODES.has(code) ? code : undefined;
}

/** "LOS ANGELES" and "los angeles" become "Los Angeles"; anything already in
 *  mixed case ("McAllen", "St. Louis") is how its author meant it. */
function titleCase(s: string): string {
  if (s !== s.toUpperCase() && s !== s.toLowerCase()) return s;
  return s
    .toLowerCase()
    .replace(/(^|[\s\-'.])(\p{L})/gu, (_, sep, ch) => sep + ch.toUpperCase())
    .replace(/(?<=\s)(And|Of|The|De|Da|Del|La|Le|Am|On|Upon)(?=\s)/g, (word) => word.toLowerCase());
}

/** A state or country written without a comma: "Brooklyn NY USA",
 *  "Portland Oregon", "New York USA". Returns the head and the tail when the
 *  last one to three words are one, and there is a head left over.
 *
 *  Stricter than a token between commas, because here the tail is cut out of
 *  running words: a two-letter code has to be capitalised ("based in" is not
 *  Indiana), and Washington and New York are not taken off the end of a name
 *  ("Port Washington", "West New York"). */
function splitTail(token: string): [string, string] | undefined {
  const words = squash(token).split(" ");
  for (let n = Math.min(3, words.length - 1); n >= 1; n--) {
    const tail = words.slice(-n).join(" ");
    const code = tail.replace(/\./g, "");
    if (code.length === 2 && !/^[A-Z]/.test(code)) continue;
    if (CITIES_NOT_STATES.has(bare(tail))) continue;
    if (countryOf(tail) || stateOf(tail)) {
      return [words.slice(0, -n).join(" "), tail];
    }
  }
  return undefined;
}

// A whole state named alone is a region in its own right, but these two are
// far more often the city.
const CITIES_NOT_STATES = new Set(["new york", "washington"]);
const STATE_NAMES = Object.fromEntries(
  Object.entries(US_STATES).map(([name, code]) => [code, titleCase(name)])
);

/** Break a token that ends in a state or country written without a comma
 *  into its parts, until nothing more comes off: "Brooklyn NY USA" is three. */
function expand(token: string): string[] {
  if (countryOf(token) || stateOf(token)) return [token];
  const split = splitTail(token);
  return split ? [...expand(split[0]), split[1]] : [token];
}

function parsePlace(raw: string): Place | undefined {
  const text = squash(
    raw
      .replace(/\([^)]*\)/g, " ") // "(USA)", "(Europe)"
      .replace(/\b\d{5}(?:-\d{4})?\b/g, " ") // zip codes
      .replace(/^.*\bbased in\s+/i, "") // "Foundation based in CA"
  );
  const parts = text
    .split(",")
    .map((t) => squash(t.replace(/^[\s\-.]+|[\s\-.]+$/g, "")))
    .filter(Boolean);
  if (parts.length === 0) return undefined;
  // Only the end of a location runs a state or country into the words before
  // it ("Brooklyn NY USA", "Chicago, IL USA"). The city at the front is left
  // whole, or "Port of Spain" loses Spain and "Port Washington" its state.
  const tokens =
    parts.length === 1
      ? expand(parts[0])
      : [...parts.slice(0, -1), ...expand(parts[parts.length - 1])];
  if (tokens.length === 0) return undefined;

  // The state and the country can sit anywhere after the city, with anything
  // after them ("Chesterfield, New Hampshire, USE"): take the last of each.
  let stateAt = -1;
  let state: string | undefined;
  let country: string | undefined;
  for (let i = 1; i < tokens.length; i++) {
    const asCountry = countryOf(tokens[i]);
    if (asCountry) country = asCountry;
    // "Chelsea, NYC": a city standing where a state would is its state.
    const asState = stateOf(tokens[i]) ?? CITY_ALIASES[bare(tokens[i])]?.[1];
    if (asState && !asCountry) {
      state = asState;
      stateAt = i;
    }
  }

  if (country && country !== "US") {
    return { city: cityOf(tokens[0]).city, qualifier: country };
  }
  if (state) {
    // The place is what stands right before the state: in "Corona, Queens
    // NY" that is Queens, which is the region somebody would look in.
    const named = cityOf(tokens[stateAt - 1]);
    return { city: named.city, qualifier: state };
  }

  const named = cityOf(tokens[0]);
  if (named.state) return { city: named.city, qualifier: named.state };
  // A state or a country on its own ("California", "UK") is the region.
  if (tokens.length === 1 || country === "US") {
    const alone = bare(tokens[0]);
    const asState = CITIES_NOT_STATES.has(alone) ? undefined : stateOf(tokens[0]);
    if (asState) return { city: STATE_NAMES[asState] ?? asState, qualifier: "" };
    const asCountry = tokens.length === 1 ? countryOf(tokens[0]) : undefined;
    if (asCountry) return { city: asCountry, qualifier: "" };
  }
  // Something after the city that is neither a state nor a country: a
  // province or region, kept so it still tells two places of one name apart.
  if (tokens.length > 1 && country !== "US") {
    return { city: named.city, qualifier: titleCase(tokens[tokens.length - 1]) };
  }
  return { city: named.city, qualifier: "" };
}

/** A city token as a name, and the state it implies when it is an alias. */
function cityOf(token: string): { city: string; state?: string } {
  const alias = CITY_ALIASES[bare(token)];
  return alias ? { city: alias[0], state: alias[1] } : { city: titleCase(token) };
}

// Names with "and" in them that are one place, not two.
const ONE_PLACE = /\b(trinidad and tobago|antigua and barbuda|bosnia and herzegovina|newfoundland and labrador|saint kitts and nevis|são tomé and príncipe)\b/gi;

// Stands in for the "and" inside such a name while the rest is split apart.
const JOINED = "~and~";

/** Somebody listing two places is in both: "/", ";", "&", "+" and "and"
 *  separate them, except inside a name that has "and" in it. */
function placesIn(location: string): Place[] {
  const protectedText = location.replace(ONE_PLACE, (name) => name.replace(/ and /gi, JOINED));
  return protectedText
    .split(/\s*(?:\/|;|\||&|\+)\s*|\s+and\s+/i)
    .map((part) => part.split(JOINED).join(" and "))
    .map(parsePlace)
    .filter((place): place is Place => Boolean(place));
}

const labelOf = (place: Place) =>
  place.qualifier ? `${place.city}, ${place.qualifier}` : place.city;

export type RegionOption = { key: string; label: string };

/**
 * The regions a set of locations falls into, and which ones each belongs to.
 *
 * A place given with no state or country ("Los Angeles", "London") joins the
 * region of that name the directory clearly means: the only one, or the one
 * with more than twice as many entries as the next. Where it is close
 * (Portland, OR and Portland, ME, one each) it stays on its own rather than
 * guessing.
 */
export function buildRegions(locations: (string | null | undefined)[]): {
  options: RegionOption[];
  keysOf: (location: string | null | undefined) => string[];
} {
  const qualified = new Map<string, Map<string, number>>();
  for (const location of locations) {
    for (const place of placesIn(location ?? "")) {
      if (!place.qualifier) continue;
      const city = place.city.toLowerCase();
      const counts = qualified.get(city) ?? new Map<string, number>();
      counts.set(labelOf(place), (counts.get(labelOf(place)) ?? 0) + 1);
      qualified.set(city, counts);
    }
  }

  const resolve = (place: Place): string => {
    if (place.qualifier) return labelOf(place);
    const counts = qualified.get(place.city.toLowerCase());
    if (!counts) return place.city;
    const [top, next] = [...counts.entries()].sort((a, b) => b[1] - a[1]);
    return !next || top[1] > 2 * next[1] ? top[0] : place.city;
  };

  const cache = new Map<string, string[]>();
  const labelsOf = (location: string | null | undefined): string[] => {
    const text = location ?? "";
    if (!cache.has(text)) {
      cache.set(text, [...new Set(placesIn(text).map(resolve))]);
    }
    return cache.get(text)!;
  };

  const labels = new Map<string, string>();
  for (const location of locations) {
    for (const label of labelsOf(location)) labels.set(label.toLowerCase(), label);
  }
  const options = [...labels.entries()]
    .map(([key, label]) => ({ key, label }))
    .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: "base" }));

  return {
    options,
    keysOf: (location) => labelsOf(location).map((label) => label.toLowerCase()),
  };
}
